import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock } from "lucide-react";
import { usePublishedBlogPosts } from "@/lib/cms";

const BlogPreviewSection = () => {
  const { data: blogPosts = [], isLoading, isError } = usePublishedBlogPosts();
  const parse = (v?: string) => {
    const t = v ? Date.parse(v) : NaN;
    return Number.isNaN(t) ? 0 : t;
  };
  const posts = [...blogPosts].sort((a, b) => parse(b.publishedAt) - parse(a.publishedAt)).slice(0, 3);
  const showLoadingCards = isLoading && posts.length === 0;

  return (
    <section className="bg-background py-10">
      <div className="container mx-auto px-4">
        <div className="mb-6 text-center">
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-2 text-base font-sans uppercase tracking-[0.2em] text-primary">Blog & Insights</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-3xl font-heading font-bold text-foreground md:text-5xl">Featured Insights from Mahirah</motion.h2>
        </div>

        <div className="mx-auto grid min-h-[21rem] max-w-5xl gap-4 md:grid-cols-3">
          {showLoadingCards && Array.from({ length: 3 }).map((_, i) => (
            <article key={`blog-loading-${i}`} className="overflow-hidden rounded-xl border border-border bg-card shadow-sm" aria-label="Loading latest blog post">
              <div className="h-44 w-full animate-pulse bg-muted" />
              <div className="space-y-3 p-4">
                <div className="h-5 w-4/5 animate-pulse rounded bg-muted" />
                <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
                <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
                <div className="h-4 w-32 animate-pulse rounded bg-muted" />
              </div>
            </article>
          ))}

          {!showLoadingCards && posts.map((post, i) => (
            <motion.article key={post.slug} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} whileHover={{ y: -4 }} className="group overflow-hidden rounded-xl border border-border bg-card shadow-sm">
              <div className="relative overflow-hidden">
                <img src={post.image} alt={post.title} className="h-44 w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                <span className="absolute left-2.5 top-2.5 flex items-center gap-1 rounded-full bg-accent px-2.5 py-0.5 text-sm font-semibold text-accent-foreground">
                  <Clock className="h-3.5 w-3.5" /> {post.readTime} read
                </span>
              </div>
              <div className="p-4">
                <h3 className="mb-1.5 text-base font-heading font-bold leading-snug text-foreground">{post.title}</h3>
                <p className="mb-1 text-sm font-sans text-muted-foreground">{post.date}</p>
                <p className="mb-3 text-sm font-sans text-muted-foreground">By Mahirah | Executive Facilitator | Soft Skills Trainer</p>
                <Link to={`/blog/${post.slug}`} className="text-base font-semibold text-primary hover:underline">Continue Reading →</Link>
              </div>
            </motion.article>
          ))}
          {!showLoadingCards && posts.length === 0 && (
            <p className="col-span-full py-10 text-center text-base text-muted-foreground">
              {isError ? "Unable to load the latest insights right now." : "Latest insights are loading."}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default BlogPreviewSection;
